/**
 * Enquiries currently stuck.
 *
 * The blocker list itself comes from `get_pipeline_analytics` — the server decides what counts as
 * blocked and how old it is, and nothing here second-guesses that. But the SCOPE is applied
 * locally: that method's `from_date` is ignored whenever `to_date` is sent (see `KpiRow`), so its
 * list is always the all-time one. Filtering it against the enquiries in range keeps this board
 * agreeing with the rest of the page.
 */
import { usePipelineAnalytics } from '../../lib/api/useAnalytics'
import { useState } from 'react'
import type { EnquiryFact } from '../../lib/analytics/types'
import { BoardCard } from './BoardCard'

export function BlockersBoard({ facts }: { facts: readonly EnquiryFact[] | undefined }) {
  // No range is sent at all: the server would ignore `from_date` anyway, and asking for everything
  // and scoping it here is honest about what is actually happening.
  const { data, isLoading, error } = usePipelineAnalytics({})

  /*
    The board's own filters (Akhil, 2026-09-19), on top of the page's range and purchaser /
    customer / type / status: which blocker, whose, and how old. Options come from the scoped
    list, so a chip never offers a reason that would show nothing.
  */
  const [reason, setReason] = useState('')
  const [owner, setOwner] = useState('')
  const [minAge, setMinAge] = useState(0)

  const inRange = new Set((facts ?? []).map((f) => f.id))
  const scoped = (data?.blockers ?? []).filter((b) => inRange.has(b.id))
  const reasons = [...new Set(scoped.map((b) => b.reason))].sort()
  const owners = [...new Set(scoped.map((b) => b.owner).filter(Boolean))].sort()
  const blockers = scoped
    .filter((b) => (reason === '' || b.reason === reason) && (owner === '' || b.owner === owner) && b.age_days >= minAge)
    .sort((a, b) => b.age_days - a.age_days)

  return (
    <BoardCard
      title="⚠ Active blockers"
      subtitle="Enquiries waiting on something, oldest first."
      isLoading={isLoading || facts === undefined}
      error={error}
      isEmpty={scoped.length === 0}
      emptyLabel="Nothing blocked in this range."
      actions={
        scoped.length > 0 ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={reason} onChange={(e) => setReason(e.target.value)} title="Blocker" style={{ fontSize: 12, padding: '3px 6px' }}>
              <option value="">Blocker: any</option>
              {reasons.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <select value={owner} onChange={(e) => setOwner(e.target.value)} title="Owner" style={{ fontSize: 12, padding: '3px 6px' }}>
              <option value="">Owner: any</option>
              {owners.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
            <select value={String(minAge)} onChange={(e) => setMinAge(Number(e.target.value))} title="Older than" style={{ fontSize: 12, padding: '3px 6px' }}>
              <option value="0">Any age</option>
              <option value="3">3+ days</option>
              <option value="7">7+ days</option>
              <option value="14">14+ days</option>
              <option value="30">30+ days</option>
            </select>
            <span style={{ color: 'var(--muted)', fontSize: 11.5 }}>
              {blockers.length} of {scoped.length}
            </span>
          </div>
        ) : undefined
      }
    >
      {blockers.length === 0 ? (
        <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 10 }}>No blocker matches these filters.</div>
      ) : null}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ marginTop: 10, width: '100%' }}>
          <thead>
            <tr>
              <th>Enquiry</th>
              <th>Customer</th>
              <th>Blocker</th>
              <th>Owner</th>
              <th className="right">Age</th>
            </tr>
          </thead>
          <tbody>
            {blockers.map((b) => (
              <tr key={b.id}>
                <td className="mono">{b.id}</td>
                <td>{b.customer}</td>
                <td><span className="badge b-dan">{b.reason}</span></td>
                <td>{b.owner}</td>
                <td className="right mono">{b.age_days}d</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </BoardCard>
  )
}
